import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAppSelector } from '../store/hooks';
import AsyncSearchSelect, { type SelectOption as AsyncOption } from './AsyncSearchSelect';
import Select, { type SelectOption } from './Select';
import LoadingSpinner from './LoadingSpinner';
import { formatDateEuropean } from '../utils/dateUtils';
import type { MentionEligibility, StudentSubscription } from '../store/slices/studentsSlice';
import './Students.css';
import './GroupForm.css';
import './RewardSubscriptionBox.css';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

type MentionRow = StudentSubscription & {
  student?: {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    surname?: string | null;
  };
  mentionEligibility: MentionEligibility;
};

type GiveawayOption = SelectOption<number | ''>;

function personName(person: MentionRow['student']) {
  if (!person) return '—';
  return [person.firstName, person.lastName, person.surname].filter(Boolean).join(' ') || `Student #${person.id}`;
}

const MentionRewards: React.FC = () => {
  const token = useAppSelector((state) => state.auth.token);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mentions, setMentions] = useState<MentionRow[]>([]);
  const [giveawayOptions, setGiveawayOptions] = useState<GiveawayOption[]>([
    { value: '', label: 'All giveaways' },
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 50,
  });

  const studentId = searchParams.get('studentId') || '';
  const dateFrom = searchParams.get('dateFrom') || '';
  const dateTo = searchParams.get('dateTo') || '';
  const giveawayId = searchParams.get('giveawayId') || '';
  const page = Math.max(parseInt(searchParams.get('page') || '1', 10), 1);

  const [studentOption, setStudentOption] = useState<AsyncOption<number> | null>(null);
  const [draftDateFrom, setDraftDateFrom] = useState(dateFrom);
  const [draftDateTo, setDraftDateTo] = useState(dateTo);
  const [draftGiveawayId, setDraftGiveawayId] = useState<number | ''>(giveawayId ? Number(giveawayId) : '');

  const loadStudents = useCallback(async (search: string): Promise<AsyncOption<number>[]> => {
    const response = await axios.get(`${API_BASE_URL}/students`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { search, includeUngrouped: true, limit: 100 },
    });
    return (response.data.students || []).map((student: {
      id: number;
      firstName?: string;
      lastName?: string;
      surname?: string;
    }) => ({
      value: student.id,
      label: [student.firstName, student.lastName, student.surname].filter(Boolean).join(' ') || `Student #${student.id}`,
    }));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE_URL}/giveaways`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { page: 1, limit: 100 },
    }).then((response) => {
      setGiveawayOptions([
        { value: '', label: 'All giveaways' },
        ...(response.data.giveaways || []).map((giveaway: { id: number; name: string }) => ({
          value: giveaway.id,
          label: giveaway.name,
        })),
      ]);
    }).catch(() => undefined);
  }, [token]);

  useEffect(() => {
    if (!studentId || studentOption?.value === Number(studentId)) return;
    loadStudents('').then((options) => {
      setStudentOption(options.find((option) => option.value === Number(studentId)) || null);
    }).catch(() => setStudentOption(null));
  }, [studentId, studentOption?.value, loadStudents]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE_URL}/mention-rewards`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        page,
        limit: 50,
        ...(studentId ? { studentId } : {}),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        ...(giveawayId ? { giveawayId } : {}),
      },
    }).then((response) => {
      setMentions(response.data.mentions || []);
      setPagination(response.data.pagination);
    }).catch((err) => {
      setError(axios.isAxiosError(err) ? err.response?.data?.error || err.message : 'Failed to load mentions');
    }).finally(() => setLoading(false));
  }, [token, page, studentId, dateFrom, dateTo, giveawayId]);

  const selectedGiveaway = useMemo(
    () => giveawayOptions.find((option) => option.value === draftGiveawayId) || giveawayOptions[0],
    [draftGiveawayId, giveawayOptions]
  );

  const applyFilters = () => {
    const next = new URLSearchParams();
    if (studentOption) next.set('studentId', String(studentOption.value));
    if (draftDateFrom) next.set('dateFrom', draftDateFrom);
    if (draftDateTo) next.set('dateTo', draftDateTo);
    if (draftGiveawayId) next.set('giveawayId', String(draftGiveawayId));
    setSearchParams(next);
  };

  const clearFilters = () => {
    setStudentOption(null);
    setDraftDateFrom('');
    setDraftDateTo('');
    setDraftGiveawayId('');
    setSearchParams(new URLSearchParams());
  };

  const changePage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage > 1) next.set('page', String(nextPage));
    else next.delete('page');
    setSearchParams(next);
  };

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Mentions</h1>
          <p>Proof history for giveaway mention rewards</p>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => navigate(studentOption ? `/mentions/create?studentId=${studentOption.value}` : '/mentions/create')}
        >
          + Add Mention
        </button>
      </div>

      <div className="filter-bar">
        <div className="mention-filter-student">
          <AsyncSearchSelect<number, AsyncOption<number>>
            value={studentOption}
            onChange={setStudentOption}
            loadOptions={loadStudents}
            placeholder="Filter by student…"
            noOptionsMessage="No students"
          />
        </div>
        <input type="date" className="form-input" value={draftDateFrom} onChange={(event) => setDraftDateFrom(event.target.value)} />
        <span className="filter-sep">-</span>
        <input type="date" className="form-input" value={draftDateTo} onChange={(event) => setDraftDateTo(event.target.value)} />
        <div className="mention-filter-eligibility">
          <Select<number | '', GiveawayOption>
            value={selectedGiveaway}
            onChange={(option) => setDraftGiveawayId(option?.value ?? '')}
            options={giveawayOptions}
            isClearable={false}
          />
        </div>
        <button type="button" className="btn-primary" onClick={applyFilters}>Apply</button>
        <button type="button" className="btn-secondary" onClick={clearFilters}>Clear</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : mentions.length === 0 ? (
        <div className="no-students"><p>No mention rewards match these filters.</p></div>
      ) : (
        <>
          <div className="results-summary">
            <p>Showing {mentions.length} of {pagination.totalItems} mention rewards</p>
          </div>
          <div className="students-table-container">
            <table className="students-table mentions-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Granted</th>
                  <th>Giveaway</th>
                  <th className="mentions-table__lessons">Lessons</th>
                  <th>Period</th>
                  <th>Proof</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mentions.map((mention) => (
                  <tr key={mention.id}>
                    <td>
                      <button type="button" className="table-link-button" onClick={() => navigate(`/students/edit/${mention.studentId}`)}>
                        {personName(mention.student)}
                      </button>
                    </td>
                    <td>{mention.createdAt ? formatDateEuropean(mention.createdAt) : '—'}</td>
                    <td>{mention.giveaway?.name || '—'}</td>
                    <td className="mentions-table__lessons">{mention.lessonsCount}</td>
                    <td className="mention-period">
                      {formatDateEuropean(mention.startDate)}
                      {' – '}
                      {mention.effectiveEndDate ? formatDateEuropean(mention.effectiveEndDate) : '—'}
                    </td>
                    <td>
                      {mention.proofUrl
                        ? (
                          <a
                            href={mention.proofUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mention-action-link"
                          >
                            View proof
                          </a>
                        )
                        : '—'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-icon"
                        data-tooltip="Edit"
                        aria-label="Edit mention"
                        onClick={() => navigate(`/mentions/edit/${mention.studentId}/${mention.id}`)}
                      >
                        ✎
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button type="button" className="btn-secondary" disabled={page <= 1} onClick={() => changePage(page - 1)}>Previous</button>
          <span>Page {pagination.currentPage} of {pagination.totalPages}</span>
          <button type="button" className="btn-secondary" disabled={page >= pagination.totalPages} onClick={() => changePage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
};

export default MentionRewards;
